import { useMemo } from 'react';
import { Tooltip } from '@heroui/react';
import { format } from 'date-fns';
import type {
  ITeacherModuleAttendanceLesson,
  ITeacherModuleAttendanceStudent,
} from '@/entities/TeacherAttendance';

type ModuleAttendanceTableProps = {
  lessons: ITeacherModuleAttendanceLesson[];
  students: ITeacherModuleAttendanceStudent[];
  valueMap: Map<string, number>;
  onChangeAttendance: (
    student: ITeacherModuleAttendanceStudent,
    lesson: ITeacherModuleAttendanceLesson,
  ) => void;
  /** Ячейка без отметки создаёт её (attendance.create), с отметкой — правит (attendance.update) */
  canCreateAttendance?: boolean;
  canUpdateAttendance?: boolean;
};

type DateColumn = {
  dateKey: string;
  dateLabel: string;
  lessons: ITeacherModuleAttendanceLesson[];
};

const getCellColor = (value: number | null | undefined) => {
  if (value === undefined || value === null) return 'bg-gray-100 text-grey-secondary';
  if (value === 1) return 'bg-[#CFF7D3] text-black';
  if (value === 0.5) return 'bg-[#FFF9C4] text-black';
  return 'bg-[#FDD3D0] text-black';
};

const getStudentName = (student: ITeacherModuleAttendanceStudent) =>
  student.firstName && student.lastName
    ? `${student.firstName} ${student.lastName}`
    : student.username;

const getLessonDateTime = (scheduledAt: string | null) => {
  if (!scheduledAt) return { date: '—', time: '—' };
  try {
    const parsed = new Date(scheduledAt);
    return {
      date: format(parsed, 'dd.MM.yyyy'),
      time: format(parsed, 'HH:mm'),
    };
  } catch {
    return { date: '—', time: '—' };
  }
};

export const ModuleAttendanceTable = ({
  lessons,
  students,
  valueMap,
  onChangeAttendance,
  canCreateAttendance = true,
  canUpdateAttendance = true,
}: ModuleAttendanceTableProps) => {
  const dateColumns = useMemo<DateColumn[]>(() => {
    const byDate = new Map<string, DateColumn>();
    const result: DateColumn[] = [];

    lessons.forEach((lesson) => {
      const { date } = getLessonDateTime(lesson.scheduledAt);
      const dateKey = date;
      const existed = byDate.get(dateKey);

      if (existed) {
        existed.lessons.push(lesson);
        return;
      }

      const nextColumn: DateColumn = {
        dateKey,
        dateLabel: date,
        lessons: [lesson],
      };
      byDate.set(dateKey, nextColumn);
      result.push(nextColumn);
    });

    return result;
  }, [lessons]);

  return (
    <div className="h-full min-h-0 w-full overflow-hidden rounded-xl border border-grey-modern bg-white">
      <div className="max-h-[70vh] h-full min-h-0 w-full overflow-auto">
        <table className="border-separate border-spacing-0 min-w-max w-max">
          <thead className="sticky top-0 z-30 bg-white">
            <tr className="bg-white">
              <th
                rowSpan={2}
                className="sticky top-0 left-0 z-30 w-[80px] min-w-[80px] bg-white text-left py-3 px-4 font-medium text-grey-secondary text-sm border-b border-grey-modern"
              >
                <div className="flex items-center gap-2">
                  <span>ID</span>
                  {/* <Svg Icon={ChevronDownIcon} width={12} height={12} /> */}
                </div>
              </th>
              <th
                rowSpan={2}
                className="sticky top-0 left-[80px] z-30 w-[220px] min-w-[220px] bg-white text-left py-3 px-4 font-medium text-grey-secondary text-sm border-b border-grey-modern"
              >
                <div className="flex items-center gap-2">
                  <span>User</span>
                </div>
              </th>
              {dateColumns.map((dateColumn) => (
                <th
                  key={dateColumn.dateKey}
                  colSpan={dateColumn.lessons.length}
                  className="border-l border-b border-grey-modern px-2 text-center text-sm font-bold text-grey-description bg-grey-10 h-[44px]"
                >
                  {dateColumn.dateLabel}
                </th>
              ))}
            </tr>
            <tr className="bg-white">
              {dateColumns.map((dateColumn) =>
                dateColumn.lessons.map((lesson) => {
                  const { time } = getLessonDateTime(lesson.scheduledAt);
                  return (
                    <th
                      key={lesson.id}
                      className="border-l border-b border-grey-modern px-2 text-center text-xs font-normal text-grey-secondary bg-gray-50 min-w-[64px] h-[44px]"
                    >
                      <Tooltip content={lesson.title} placement="top">
                        <span className="cursor-default">{time}</span>
                      </Tooltip>
                    </th>
                  );
                }),
              )}
            </tr>
          </thead>
          <tbody>
            {students.map((student, index) => (
              <tr
                key={student.enrollmentId}
                className={`group hover:bg-grey-10/50 ${index === students.length - 1 ? '[&>td]:border-b-0' : ''}`}
              >
                <td className="sticky left-0 z-10 w-[80px] min-w-[80px] bg-white group-hover:bg-grey-10/50 py-3 px-4 text-black-headline font-medium border-b border-grey-modern">
                  {student.enrollmentId}
                </td>
                <td className="sticky left-[80px] z-10 w-[220px] min-w-[220px] bg-white group-hover:bg-grey-10/50 py-3 px-4 border-b border-grey-modern">
                  <div className="flex flex-col">
                    <span className="text-black-headline font-medium">
                      {getStudentName(student)}
                    </span>
                    <span className="text-grey-secondary text-sm">
                      {student.email}
                    </span>
                  </div>
                </td>
                {lessons.map((lesson) => {
                  const value = valueMap.get(
                    `${student.enrollmentId}-${lesson.groupLessonId}`,
                  );
                  const displayValue =
                    value === undefined || value === null
                      ? '—'
                      : value === 0
                        ? '0.0'
                        : String(value);
                  const hasValue = value !== undefined && value !== null;
                  const canChange = hasValue ? canUpdateAttendance : canCreateAttendance;

                  return (
                    <td
                      key={`${student.enrollmentId}-${lesson.groupLessonId}`}
                      className="border border-white p-0 text-center align-middle min-w-[64px] h-[44px]"
                    >
                      {lesson.hasAttendanceSet && canChange ? (
                        <button
                          type="button"
                          onClick={() => onChangeAttendance(student, lesson)}
                          className={`w-full h-full min-h-[44px] flex items-center justify-center font-medium transition-opacity hover:opacity-90 cursor-pointer ${getCellColor(value)}`}
                        >
                          {displayValue}
                        </button>
                      ) : lesson.hasAttendanceSet ? (
                        <span
                          className={`w-full h-full min-h-[44px] inline-flex items-center justify-center font-medium ${getCellColor(value)}`}
                        >
                          {displayValue}
                        </span>
                      ) : (
                        <span
                          className={`w-full h-full min-h-[44px] inline-flex items-center justify-center font-medium ${getCellColor(null)}`}
                        >
                          —
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
